import React from 'react';
import { motion } from 'framer-motion';

const SEOLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Stop Feeling Guilty After Shopping
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Shopping Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Guilt Gauge helps you understand your emotional spending patterns and reduce shopping regret.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-8xl">🛍️</div>
        </div>
      </div>
      
      {/* What causes shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Feel Guilty After Shopping?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Budget Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Spending more than planned can trigger financial anxiety and post-purchase guilt.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Emotional Shopping</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping to boost mood or combat boredom often leads to regret later.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👀</span>
              <div>
                <h3 className="font-bold dark:text-white">FOMO Purchases</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Buying due to fear of missing out rather than actual need or desire.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🤔</span>
              <div>
                <h3 className="font-bold dark:text-white">Value Misalignment</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Purchases that don't align with personal values or long-term goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Guilt Gauge Helps You Shop Without Regret</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Shopping Emotions
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Log how you felt during purchases to identify emotional spending triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Guilt Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get a personalized assessment of each purchase based on multiple factors.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Personalized Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored tips to reduce guilt and develop healthier spending habits.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              See Your Shopping Personality
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Discover your unique buying patterns and shopping triggers.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Overcame Shopping Guilt</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "After using Guilt Gauge for a month, I stopped making those 'sad day' purchases that I always regretted. My guilt score dropped from 75 to under 30!"
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jessica T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Emotional Spender</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was constantly feeling buyer's remorse until I started tracking my purchases. Now I understand my triggers and have practical strategies to avoid regret."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Impulse Buyer</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Shopping Guilt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is feeling guilty after shopping normal?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, shopping guilt is very common. Studies show that up to 80% of consumers have experienced post-purchase regret, especially with unplanned or emotional purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stop spending money on unnecessary things?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Track your spending habits with Guilt Gauge, implement the 24-hour rule for impulse purchases, and set clear budget goals. Understanding your emotional triggers is the first step to changing your behavior.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What is buyer's remorse and how can I avoid it?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Buyer's remorse is the feeling of regret after making a purchase. To avoid it, ask yourself if the item aligns with your values, wait before buying, and use Guilt Gauge to understand your personal spending patterns.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is Guilt Gauge free to use?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes! Guilt Gauge is completely free, with no ads or premium features. Your data stays private on your device, and we don't sell your information.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Shop Without the Guilt?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your purchases today and discover your personal shopping patterns.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Guilt Score Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping Guilt</h2>
          <p className="mb-3">
            Shopping guilt, also known as buyer's remorse or post-purchase anxiety, affects millions of people. It's the feeling of regret, anxiety, or guilt that occurs after making a purchase. This emotional response is often triggered by concerns about spending too much, making an impulsive decision, or buying something unnecessary.
          </p>
          <p className="mb-3">
            Guilt Gauge helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to make more satisfying shopping decisions.
          </p>
          <p>
            Whether you're dealing with impulse shopping, emotional spending, or budget concerns, our free tool helps you develop healthier habits and reduce shopping guilt once and for all.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default SEOLandingPage;
